import * as THREE from 'three';
import { MorpheusMap } from './map/MorpheusMap';
import { WeaponSystem, WeaponType } from './weapons/WeaponSystem';
import { FPSController } from './player/FPSController';
import { BotAI, BOT_CONFIGS } from './bots/BotAI';
import { fpsAudio } from './audio/FPSAudio';

export interface KillfeedMessage {
  id: string;
  killer: string;
  victim: string;
  weapon: string;
  timestamp: number;
}

export interface ScoreEntry {
  name: string;
  frags: number;
  deaths: number;
  ping: number;
  isPlayer: boolean;
}

export class FPSGame {
  public scene: THREE.Scene;
  public camera: THREE.PerspectiveCamera;
  public renderer: THREE.WebGLRenderer;

  public map: MorpheusMap;
  public weapons: WeaponSystem;
  public player: FPSController;
  public bots: BotAI[] = [];

  // Match state
  public killfeed: KillfeedMessage[] = [];
  public bannerMessage: string = '';
  public bannerTimer: number = 0;
  public matchTime: number = 600; // 10 minutes match
  public fragLimit: number = 20;
  public isMatchOver: boolean = false;
  public winnerName: string = '';

  private playerConsecutiveKills: number = 0;
  private lastPlayerKillTime: number = 0;
  private firstBloodOccurred: boolean = false;

  private clock: THREE.Clock = new THREE.Clock();

  constructor(container: HTMLElement) {
    // 1. Scene setup
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0c1424);
    this.scene.fog = new THREE.FogExp2(0x0c1424, 0.004);

    // 2. Camera
    this.camera = new THREE.PerspectiveCamera(85, window.innerWidth / window.innerHeight, 0.1, 1000);
    this.scene.add(this.camera);

    // 3. Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    container.appendChild(this.renderer.domElement);

    // 4. Map & Scene Lighting
    // Ambient & Hemisphere sky fill
    const hemiLight = new THREE.HemisphereLight(0xa0c4ff, 0x3a4556, 1.8);
    this.scene.add(hemiLight);

    const ambientLight = new THREE.AmbientLight(0x404d66, 1.0);
    this.scene.add(ambientLight);

    // Primary High Celestial Sun
    const sunLight = new THREE.DirectionalLight(0xfff5e6, 2.5);
    sunLight.position.set(60, 90, 45);
    this.scene.add(sunLight);

    // Secondary Cool Blue Rim / Back Light
    const rimLight = new THREE.DirectionalLight(0x38bdf8, 1.4);
    rimLight.position.set(-60, 50, -45);
    this.scene.add(rimLight);

    this.map = new MorpheusMap();
    this.scene.add(this.map.group);

    // 5. Weapon System
    this.weapons = new WeaponSystem(this.scene);

    // 6. Player Controller
    this.player = new FPSController(this.camera, this.renderer.domElement);
    const playerSpawn = this.map.spawnPoints[0];
    this.player.respawn(playerSpawn);

    // Player death listener
    this.player.onPlayerDeath = (reason, killerId) => {
      this.playerConsecutiveKills = 0;
      const killerName = this.getCombatantName(killerId || 'world');
      this.addKillfeed(killerName, 'Player', 'Combat');
    };

    // 7. Spawn Iconic AI Bots
    const botSpawns = this.map.spawnPoints.slice(1);
    BOT_CONFIGS.slice(0, 5).forEach((cfg, idx) => {
      const spawn = botSpawns[idx % botSpawns.length] || new THREE.Vector3(0, 28, 0);
      const bot = new BotAI(`bot_${idx}`, cfg, spawn);
      this.bots.push(bot);
      this.scene.add(bot.mesh);
    });

    // Window resize handler
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
  }

  public getCombatantName(id: string): string {
    if (id === 'player') return 'Player';
    const bot = this.bots.find((b) => b.id === id);
    return bot ? bot.name : 'The Abyss';
  }

  public addKillfeed(killer: string, victim: string, weapon: string) {
    this.killfeed.unshift({
      id: Math.random().toString(36).substring(2, 9),
      killer,
      victim,
      weapon,
      timestamp: Date.now(),
    });

    if (this.killfeed.length > 6) {
      this.killfeed.pop();
    }
  }

  public showBanner(msg: string) {
    this.bannerMessage = msg;
    this.bannerTimer = 3.0;
  }

  public start() {
    this.clock.start();
    this.animate();
  }

  private animate = () => {
    requestAnimationFrame(this.animate);
    const deltaTime = Math.min(this.clock.getDelta(), 0.1);
    this.update(deltaTime);
    this.renderer.render(this.scene, this.camera);
  };

  private update(deltaTime: number) {
    if (this.isMatchOver) return;

    // Match timer
    this.matchTime -= deltaTime;
    if (this.matchTime <= 0) {
      this.matchTime = 0;
      this.checkMatchEnd();
    }

    if (this.bannerTimer > 0) {
      this.bannerTimer -= deltaTime;
      if (this.bannerTimer <= 0) {
        this.bannerMessage = '';
      }
    }

    // 1. Update Map
    this.map.update(deltaTime);

    // 2. Update Weapons
    this.weapons.update(deltaTime);

    // 3. Update Player
    this.player.update(deltaTime, this.map, this.weapons);

    // 4. Update Bots
    const combatants = [
      { id: 'player', getPosition: () => this.player.camera.position, isAlive: this.player.isAlive },
      ...this.bots.map((b) => ({ id: b.id, getPosition: () => b.mesh.position, isAlive: b.isAlive })),
    ];

    for (const bot of this.bots) {
      bot.update(deltaTime, this.map, this.weapons, combatants);
    }

    // 5. Handle Projectile & Explosions Collision
    this.handleCombatHits();

    // Clean up killfeed messages older than 7s
    const now = Date.now();
    this.killfeed = this.killfeed.filter((item) => now - item.timestamp < 7000);
  }

  private handleCombatHits() {
    const projectiles = this.weapons.projectiles;

    // A. Direct Projectile Collisions
    for (let pIdx = projectiles.length - 1; pIdx >= 0; pIdx--) {
      const proj = projectiles[pIdx];
      let hasHit = false;

      // Check hit on player
      if (proj.ownerId !== 'player' && this.player.isAlive) {
        const playerPos = this.player.camera.position.clone().add(new THREE.Vector3(0, -0.9, 0));
        if (proj.mesh.position.distanceTo(playerPos) < proj.radius + 0.6) {
          const res = this.player.takeDamage(proj.damage, proj.ownerId);
          if (res.killed) {
            this.handleKill(proj.ownerId, 'player', proj.type);
          }
          hasHit = true;
        }
      }

      // Check hit on bots
      if (!hasHit) {
        for (const bot of this.bots) {
          if (!bot.isAlive || bot.id === proj.ownerId) continue;
          const botCenter = bot.mesh.position.clone().add(new THREE.Vector3(0, 1.0, 0));
          if (proj.mesh.position.distanceTo(botCenter) < proj.radius + 0.65) {
            const res = bot.takeDamage(proj.damage, proj.ownerId);
            if (res.killed) {
              this.handleKill(proj.ownerId, bot.id, proj.type);
            }
            hasHit = true;
            break;
          }
        }
      }

      // Check hit on map walls/floors for non-bouncing or bouncing projectiles
      if (!hasHit) {
        for (const col of this.map.colliders) {
          if (col.box.containsPoint(proj.mesh.position)) {
            if (proj.isBouncing) {
              proj.velocity.y *= -0.7;
              proj.velocity.x *= 0.7;
              proj.velocity.z *= 0.7;
            } else {
              hasHit = true;
            }
            break;
          }
        }
      }

      if (hasHit) {
        this.weapons.removeProjectile(pIdx);
      }
    }

    // B. Explosions (Splash Damage & Knockback)
    for (const exp of this.weapons.activeExplosions) {
      // Affect player
      if (this.player.isAlive) {
        const dist = this.player.camera.position.distanceTo(exp.position);
        if (dist < exp.radius) {
          const falloff = 1 - dist / exp.radius;
          const dmg = exp.damage * falloff;
          const knockback = this.player.camera.position.clone().sub(exp.position).normalize().multiplyScalar(18 * falloff);
          this.player.velocity.add(knockback);

          const res = this.player.takeDamage(dmg, exp.ownerId);
          if (res.killed) {
            this.handleKill(exp.ownerId, 'player', 'Rocket Launcher');
          }
        }
      }

      // Affect bots
      for (const bot of this.bots) {
        if (!bot.isAlive) continue;
        const dist = bot.mesh.position.distanceTo(exp.position);
        if (dist < exp.radius) {
          const falloff = 1 - dist / exp.radius;
          const dmg = exp.damage * falloff;
          const knockback = bot.mesh.position.clone().sub(exp.position).normalize().multiplyScalar(18 * falloff);
          bot.velocity.add(knockback);

          const res = bot.takeDamage(dmg, exp.ownerId);
          if (res.killed) {
            this.handleKill(exp.ownerId, bot.id, 'Rocket Launcher');
          }
        }
      }
    }
  }

  private handleKill(killerId: string, victimId: string, weaponType: string) {
    const killerName = this.getCombatantName(killerId);
    const victimName = this.getCombatantName(victimId);

    // Format readable weapon name
    const weaponLabel =
      weaponType === 'bullet'
        ? 'Enforcer'
        : weaponType === 'shock_orb' || weaponType === 'shock'
        ? 'Shock Rifle'
        : weaponType === 'flak_shard'
        ? 'Flak Cannon'
        : weaponType === 'rocket'
        ? 'Rocket Launcher'
        : weaponType === 'pulse'
        ? 'Pulse Gun'
        : 'Sniper Rifle';

    this.addKillfeed(killerName, victimName, weaponLabel);

    if (victimId === 'player') {
      this.player.killerName = killerName;
      this.player.deathReason = killerId === 'world' ? 'Fell into the abyss' : `Fragged with ${weaponLabel}`;
    }

    // Announcer & Kill Streaks
    if (!this.firstBloodOccurred) {
      this.firstBloodOccurred = true;
      this.showBanner('FIRST BLOOD!');
      fpsAudio.playSound('frag');
    }

    if (killerId === 'player') {
      this.player.score++;
      fpsAudio.playSound('frag');

      const now = performance.now();
      const timeSinceLastKill = (now - this.lastPlayerKillTime) / 1000;
      this.lastPlayerKillTime = now;
      this.playerConsecutiveKills++;

      // Multi-kills
      if (timeSinceLastKill < 3.5) {
        if (this.playerConsecutiveKills === 2) this.showBanner('DOUBLE KILL!');
        else if (this.playerConsecutiveKills === 3) this.showBanner('MULTI KILL!');
        else if (this.playerConsecutiveKills === 4) this.showBanner('ULTRA KILL!');
        else if (this.playerConsecutiveKills >= 5) this.showBanner('MONSTER KILL!');
      } else {
        // Spree milestones
        if (this.player.score === 5) this.showBanner('KILLING SPREE!');
        else if (this.player.score === 10) this.showBanner('RAMPAGE!');
        else if (this.player.score === 15) this.showBanner('UNSTOPPABLE!');
        else if (this.player.score === 20) this.showBanner('GODLIKE!');
      }

      if (this.player.score >= this.fragLimit) {
        this.finishMatch('Player');
      }
    } else {
      const killerBot = this.bots.find((b) => b.id === killerId);
      if (killerBot) {
        killerBot.score++;
        if (killerBot.score >= this.fragLimit) {
          this.finishMatch(killerBot.name);
        }
      }
    }
  }

  private finishMatch(winner: string) {
    this.isMatchOver = true;
    this.winnerName = winner;
    this.showBanner(`MATCH FINISHED - ${winner.toUpperCase()} WINS!`);
  }

  private checkMatchEnd() {
    let topScore = this.player.score;
    let topName = 'Player';
    for (const bot of this.bots) {
      if (bot.score > topScore) {
        topScore = bot.score;
        topName = bot.name;
      }
    }
    this.finishMatch(topName);
  }

  public getScoreboard(): ScoreEntry[] {
    const list: ScoreEntry[] = [
      { name: 'Player', frags: this.player.score, deaths: this.player.deaths, ping: 0, isPlayer: true },
      ...this.bots.map((b) => ({
        name: b.name,
        frags: b.score,
        deaths: b.deaths,
        ping: Math.floor(18 + Math.random() * 8),
        isPlayer: false,
      })),
    ];

    list.sort((a, b) => b.frags - a.frags);
    return list;
  }
}
